<template>
  <div class="article-navigation" v-if="hasContent">
    <!-- 上一篇 / 下一篇 -->
    <div class="adjacent-articles" v-if="prev || next">
      <NuxtLink 
        v-if="prev" 
        :to="`/blog/${prev.Slug}`" 
        class="nav-card prev-article"
      >
        <div class="nav-direction">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          <span>上一篇</span>
        </div>
        <div class="nav-content">
          <h4 class="nav-title">{{ prev.Title }}</h4>
          <p class="nav-summary" v-if="prev.Summary">{{ truncateSummary(prev.Summary) }}</p>
        </div>
      </NuxtLink>
      <div v-else class="nav-card placeholder"></div>

      <NuxtLink 
        v-if="next" 
        :to="`/blog/${next.Slug}`" 
        class="nav-card next-article"
      >
        <div class="nav-direction">
          <span>下一篇</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
        <div class="nav-content">
          <h4 class="nav-title">{{ next.Title }}</h4>
          <p class="nav-summary" v-if="next.Summary">{{ truncateSummary(next.Summary) }}</p>
        </div>
      </NuxtLink>
      <div v-else class="nav-card placeholder"></div>
    </div>

    <!-- 相关文章推荐 -->
    <div class="related-articles" v-if="related && related.length > 0">
      <h3 class="related-title">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
          <line x1="7" y1="7" x2="7.01" y2="7"></line>
        </svg>
        <span>相关文章</span>
      </h3>
      <div class="related-list">
        <NuxtLink 
          v-for="article in related" 
          :key="article.ArticleId"
          :to="`/blog/${article.Slug}`"
          class="related-item"
        >
          <div class="related-cover" v-if="article.CoverImage">
            <BlurhashImage
              :src="getImageUrl(article.CoverImage)"
              :hash="article.CoverBlurHash || 'L6PZfSi_.AyE_3t7t7R**0o#DgR4'"
              :alt="article.Title"
              class="related-img"
            />
          </div>
          <div class="related-cover placeholder-cover" v-else>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div class="related-info">
            <h5 class="related-article-title">{{ article.Title }}</h5>
            <span class="related-type" v-if="article.TypeName">{{ article.TypeName }}</span>
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface ArticleNav {
  ArticleId: number
  Title: string
  Slug: string
  Summary?: string | null
  CoverImage?: string | null
  CoverBlurHash?: string | null
  TypeName?: string
  PublishedAt?: string
}

interface Props {
  prev?: ArticleNav | null
  next?: ArticleNav | null
  related?: ArticleNav[]
}

const props = withDefaults(defineProps<Props>(), {
  prev: null,
  next: null,
  related: () => []
})

const hasContent = computed(() => {
  return props.prev || props.next || (props.related && props.related.length > 0)
})

const truncateSummary = (summary: string, maxLength: number = 60) => {
  if (!summary) return ''
  if (summary.length <= maxLength) return summary
  return summary.slice(0, maxLength) + '...'
}

const getImageUrl = (key: string) => {
  if (!key) return ''
  if (key.startsWith('http')) return key
  const imageBase = 'https://image.lixiang.page'
  return `${imageBase}/${key}`
}
</script>

<style scoped>
.article-navigation {
  max-width: 800px;
  margin: 60px auto 0;
  /* padding: 0 20px 40px; */
}

/* 上一篇 / 下一篇 */
.adjacent-articles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 40px;
}

.nav-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  text-decoration: none;
  color: var(--text-color);
  transition: all 0.2s ease;
  background: var(--panel-bg);
}

.nav-card:hover {
  border-color: var(--text-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.nav-card.placeholder {
  visibility: hidden;
}

.nav-direction {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--muted-color);
  margin-bottom: 10px;
}

.prev-article .nav-direction {
  justify-content: flex-start;
}

.next-article .nav-direction {
  justify-content: flex-end;
}

.next-article {
  text-align: right;
}

.nav-content {
  flex: 1;
}

.nav-title {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  margin: 0 0 8px 0;
  color: var(--text-color);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.nav-summary {
  font-size: 0.85rem;
  color: var(--muted-color);
  margin: 0;
  line-height: 1.5;
}

/* 相关文章推荐 */
.related-articles {
  border-top: 1px solid var(--border-color);
  padding-top: 30px;
}

.related-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-color);
  margin: 0 0 20px 0;
}

.related-title svg {
  color: var(--muted-color);
}

.related-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.related-item {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  background: var(--panel-bg);
}

.related-item:hover {
  border-color: var(--text-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.related-cover {
  width: 100%;
  height: 120px;
  overflow: hidden;
  background: rgba(125, 125, 125, 0.08);
}

.related-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-cover {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-color);
}

.related-info {
  padding: 12px;
}

.related-article-title {
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
  margin: 0 0 6px 0;
  color: var(--text-color);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-type {
  font-size: 0.75rem;
  color: var(--muted-color);
}

/* 移动端适配 */
@media (max-width: 640px) {
  .article-navigation {
    margin-top: 40px;
    /* padding: 0 16px 30px; */
  }

  .adjacent-articles {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .nav-card.placeholder {
    display: none;
  }

  .next-article {
    text-align: left;
  }

  .next-article .nav-direction {
    justify-content: flex-start;
    flex-direction: row-reverse;
  }

  .related-list {
    grid-template-columns: 1fr;
  }

  .related-item {
    flex-direction: row;
    align-items: stretch;
  }

  .related-cover {
    width: 100px;
    height: auto;
    min-height: 80px;
    flex-shrink: 0;
  }

  .related-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 10px 12px;
  }
}
</style>
